import React, { useState, useEffect, useRef, useMemo } from 'react';
import { kbWebScraper, agents as agentsApi } from '../api';
import LoadingSpinner from './LoadingSpinner';
import AnimatedDropdown from './AnimatedDropdown';
import KbAgentReviewModal from './KbAgentReviewModal';
import { MonitorCard, monitorInfo } from './WebsiteMonitorParts';
import {
  Globe, Search, X, AlertTriangle, CheckCircle, Download, Eye, Files, RotateCcw, XCircle, Info,
  Sparkles, Cpu, RefreshCw, Trash2, Pencil, FolderOpen, Bot, Clock, Coins, FileText, Ban, Plus, BellRing
} from 'lucide-react';

const JOB_STORAGE_KEY = 'kbWebScraper.lastJobId';

// Mirrors the API config; replaced by GET /knowledge-base/web-scraper/config on load
const FALLBACK_CONFIG = {
  configured: true,
  max_pages: 100,
  concurrency: 5,
  est_credits_per_page: 27,
  est_seconds_per_page: 90,
  default_model: 'claude-opus-5-5',
  models: [
    { id: 'claude-opus-5-5', label: 'Claude Opus 5.5', price_per_100kb: 1.31 },
    { id: 'gpt-5.5', label: 'GPT-5.5', price_per_100kb: 1.40 },
  ],
  confirm_above_usd: 5,
  max_ai_tokens: 150000,
};

const formatBytes = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : 'Never');

const minutesText = (seconds) => {
  const m = Math.max(1, Math.round(seconds / 60));
  return m === 1 ? 'about a minute' : `about ${m} minutes`;
};

const readStoredJob = () => {
  try { return localStorage.getItem(JOB_STORAGE_KEY); } catch { return null; }
};
const storeJob = (jobId) => {
  try {
    if (jobId) localStorage.setItem(JOB_STORAGE_KEY, jobId);
    else localStorage.removeItem(JOB_STORAGE_KEY);
  } catch { /* storage unavailable: progress just won't survive a reload */ }
};

const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const filenameFrom = (response, fallback) => {
  const header = response?.headers?.['content-disposition'] || '';
  const match = header.match(/filename="?([^"]+)"?/);
  return match ? match[1] : fallback;
};

const errorText = async (error) => {
  let detail = error?.response?.data?.detail;
  // Blob error bodies need decoding before the API message is readable
  if (!detail && error?.response?.data instanceof Blob) {
    try { detail = JSON.parse(await error.response.data.text()).detail; } catch { /* keep generic */ }
  }
  return typeof detail === 'string' ? detail : (error?.message || 'Something went wrong');
};

const cardClass = 'bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant';
const inputClass = 'w-full px-4 py-3 bg-muted/30 border border-border/60 rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all duration-300';
const primaryBtn = 'px-6 py-3 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 font-medium btn-hover-gradient';
const secondaryBtn = 'px-4 py-2 rounded-lg text-sm font-medium border border-border/60 text-foreground hover:border-primary/40 hover:bg-muted/40 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed';
const iconBtn = 'p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted/40 transition-colors disabled:opacity-50';

const Toggle = ({ checked, onChange, label, hint }) => (
  <label className="flex items-start gap-3 cursor-pointer select-none">
    <input type="checkbox" className="mt-1 w-4 h-4 accent-[hsl(var(--primary))]" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span>
      <span className="block text-sm font-medium text-foreground">{label}</span>
      {hint && <span className="block text-xs text-muted-foreground mt-0.5">{hint}</span>}
    </span>
  </label>
);

const SectionTitle = ({ icon: Icon, children, right }) => (
  <div className="flex items-center justify-between gap-3 mb-4">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
        <Icon className="w-5 h-5 text-primary-foreground" />
      </div>
      <h3 className="text-xl font-semibold text-foreground">{children}</h3>
    </div>
    {right}
  </div>
);

const STATUS_STYLES = {
  completed: 'bg-success/15 text-success',
  partial: 'bg-warning/20 text-warning',
  failed: 'bg-destructive/15 text-destructive',
};
const STATUS_LABELS = { completed: 'Up to date', partial: 'Some pages failed', failed: 'Last sync failed' };

const PAGE_STATUS = {
  pending: { label: 'Waiting', className: 'text-muted-foreground' },
  running: { label: 'Scraping', className: 'text-primary' },
  done: { label: 'Done', className: 'text-success' },
  failed: { label: 'Failed', className: 'text-destructive' },
  skipped: { label: 'Skipped', className: 'text-muted-foreground' },
};

// File list + TOPIC preview, shared by scrape results and saved websites
const FilesViewer = ({ files, emptyText }) => {
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    setSelected(files?.[0]?.filename || null);
    setFilter('');
  }, [files]);

  const current = files?.find((f) => f.filename === selected) || files?.[0];
  const blocks = useMemo(() => {
    if (!current?.content) return [];
    const all = current.content.split(/\n(?======)/);
    const q = filter.trim().toLowerCase();
    return q ? all.filter((b) => b.toLowerCase().includes(q)) : all;
  }, [current, filter]);

  if (!files?.length) {
    return <div className={`${cardClass} text-sm text-muted-foreground`}>{emptyText}</div>;
  }

  const downloadOne = (f) => saveBlob(new Blob([f.content], { type: 'text/plain;charset=utf-8' }), f.filename);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[300px_1fr] gap-4 min-h-0">
      <div className="space-y-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
        {files.map((f) => (
          <div
            key={f.filename}
            onClick={() => { setSelected(f.filename); setFilter(''); }}
            className={`cursor-pointer rounded-xl border p-4 transition-all duration-300 ${current?.filename === f.filename ? 'border-primary bg-primary/10' : 'border-border/60 bg-card/95 hover:border-primary/40'}`}
          >
            <div className="flex items-start gap-3">
              <Files className="w-4 h-4 text-primary flex-shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground break-all">{f.filename}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {formatBytes(f.bytes)} · {f.topics} topics · ~{f.estimated_chunks} chunks
                </div>
                {f.categories?.length > 0 && (
                  <div className="text-xs text-muted-foreground truncate" title={f.categories.join(', ')}>{f.categories.join(', ')}</div>
                )}
              </div>
              <button onClick={(e) => { e.stopPropagation(); downloadOne(f); }} className="text-muted-foreground hover:text-primary" title="Download this file">
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {current && (
        <div className={`${cardClass} flex flex-col min-h-0`} style={{ maxHeight: 'calc(100vh - 260px)' }}>
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <Eye className="w-5 h-5 text-primary flex-shrink-0" />
              <h3 className="text-lg font-semibold text-foreground truncate">{current.filename}</h3>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter topics"
                className="w-full pl-9 pr-3 py-2 text-sm bg-muted/30 border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          </div>
          {current.largest_topic_tokens != null && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
              <Info className="w-3.5 h-3.5" />
              Largest topic is {current.largest_topic_tokens} tokens (the retriever chunks at 400). {filter && `${blocks.length} matching topic${blocks.length === 1 ? '' : 's'}.`}
            </div>
          )}
          <pre className="flex-1 overflow-auto text-xs leading-relaxed font-mono whitespace-pre-wrap break-words bg-background/50 border border-border/60 rounded-lg p-4 text-foreground">
            {blocks.length ? blocks.join('\n') : 'No topics match this filter.'}
          </pre>
        </div>
      )}
    </div>
  );
};

const VariantTabs = ({ variant, onChange, hasAi, aiStale }) => (
  <div className="inline-flex rounded-lg border border-border/60 p-1 bg-muted/20 mb-4">
    {[{ id: 'formatted', label: 'Formatted', icon: FileText }, { id: 'ai', label: 'AI-optimized', icon: Sparkles }].map((t) => (
      <button
        key={t.id}
        onClick={() => onChange(t.id)}
        disabled={t.id === 'ai' && !hasAi}
        className={`px-4 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed ${variant === t.id ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted/40'}`}
      >
        <t.icon className="w-4 h-4" /> {t.label}
        {t.id === 'ai' && aiStale && <span className="text-[10px] px-1.5 rounded bg-warning/30 text-foreground">older</span>}
      </button>
    ))}
  </div>
);

const KbWebScraper = () => {
  const [config, setConfig] = useState(FALLBACK_CONFIG);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Saved websites
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [agentList, setAgentList] = useState([]);
  const [openSite, setOpenSite] = useState(null);       // full site with files
  const [siteVariant, setSiteVariant] = useState('formatted');
  const [busySite, setBusySite] = useState('');          // site id with an action in flight
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [reviewSite, setReviewSite] = useState(null);     // { site, variant } for the agent KB review

  // New scrape / edit pages
  const [url, setUrl] = useState('');
  const [discovering, setDiscovering] = useState(false);
  const [discovery, setDiscovery] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [pageFilter, setPageFilter] = useState('');
  const [siteName, setSiteName] = useState('');
  const [saveSite, setSaveSite] = useState(true);
  const [agentId, setAgentId] = useState('');
  const [editingSite, setEditingSite] = useState(null);  // saved site whose pages are being changed
  const [starting, setStarting] = useState(false);

  // Job
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [jobVariant, setJobVariant] = useState('formatted');
  const [downloading, setDownloading] = useState('');

  // AI step
  const [model, setModel] = useState(FALLBACK_CONFIG.default_model);
  const [generateFaqs, setGenerateFaqs] = useState(true);
  const [confirmingCost, setConfirmingCost] = useState(false);
  const [startingAi, setStartingAi] = useState(false);

  const pollRef = useRef(null);
  const lastJobRef = useRef(null);
  const syncJobsRef = useRef(new Set());  // jobs started by Sync: open the agent KB review when they finish
  const sitesRef = useRef([]);
  useEffect(() => { sitesRef.current = sites; }, [sites]);
  const [pollKey, setPollKey] = useState(0);

  const loadSites = async () => {
    try {
      const response = await kbWebScraper.listSites();
      setSites(Array.isArray(response.data) ? response.data : []);
    } catch (e) {
      setError(await errorText(e));
    } finally {
      setSitesLoading(false);
    }
  };

  useEffect(() => {
    const stored = readStoredJob();
    if (stored) setJobId(stored);
    kbWebScraper.getConfig()
      .then((response) => {
        setConfig({ ...FALLBACK_CONFIG, ...response.data });
        if (response.data?.default_model) setModel(response.data.default_model);
      })
      .catch(() => { /* keep the built-in defaults */ });
    agentsApi.list()
      .then((response) => setAgentList(Array.isArray(response.data) ? response.data : []))
      .catch(() => setAgentList([]));
    loadSites();
    return () => clearTimeout(pollRef.current);
  }, []);

  // Poll the job while the scrape or its AI step runs
  useEffect(() => {
    if (!jobId) return undefined;
    let cancelled = false;
    const poll = async () => {
      try {
        const response = await kbWebScraper.getJob(jobId);
        if (cancelled) return;
        const data = response.data;
        const prev = lastJobRef.current;
        lastJobRef.current = data;
        setJob(data);
        // A scrape or AI step that just finished may have updated a saved website
        const finished = prev && ['running', 'queued'].includes(prev.status) && !['running', 'queued'].includes(data.status);
        const aiFinished = prev && prev.ai?.status === 'running' && data.ai?.status !== 'running';
        if ((finished || aiFinished) && data.site_id) loadSites();
        if (finished && data.status === 'completed' && data.site_id && syncJobsRef.current.has(jobId)) {
          syncJobsRef.current.delete(jobId);
          const known = sitesRef.current.find((s) => s.id === data.site_id);
          setReviewSite({ site: { id: data.site_id, name: data.site_name, agent_id: known?.agent_id || null }, variant: 'formatted' });
        }
        if (aiFinished && data.ai?.status === 'completed') setJobVariant('ai');
        if (['queued', 'running'].includes(data.status) || data.ai?.status === 'running') {
          pollRef.current = setTimeout(poll, 2500);
        }
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          storeJob(null);
          setJobId(null);
          setJob(null);
        } else {
          pollRef.current = setTimeout(poll, 5000);
        }
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(pollRef.current); };
  }, [jobId, pollKey]);

  useEffect(() => { setConfirmingCost(false); }, [model, jobId]);

  // Refresh while a change check runs, so its result shows without a reload
  useEffect(() => {
    if (!sites.some((s) => s.monitor_checking) && !openSite?.monitor_checking) return undefined;
    const timer = setTimeout(async () => {
      loadSites();
      if (openSite?.monitor_checking) {
        try { setOpenSite((await kbWebScraper.getSite(openSite.id)).data); } catch { /* keep the old view */ }
      }
    }, 4000);
    return () => clearTimeout(timer);
  }, [sites, openSite]);

  const agentName = (id) => agentList.find((a) => a.id === id)?.name;

  // ── Discovery ─────────────────────────────────────────────────────────────
  const runDiscovery = async (target, preselect) => {
    setError('');
    setDiscovery(null);
    setPageFilter('');
    setDiscovering(true);
    try {
      const response = await kbWebScraper.discover(target);
      const data = response.data;
      // Pages saved earlier that discovery no longer finds stay listed so they can be kept or dropped
      if (preselect?.length) {
        const known = new Set(data.pages.map((p) => p.url));
        const missing = preselect.filter((p) => !known.has(p.url)).map((p) => ({ url: p.url, name: p.name, title: '', saved_only: true }));
        data.pages = [...data.pages, ...missing];
        data.count = data.pages.length;
        setSelected(new Set(preselect.map((p) => p.url)));
      } else {
        setSelected(new Set(data.pages.slice(0, Math.min(10, config.max_pages)).map((p) => p.url)));
        setSiteName(data.site_name || '');
      }
      setDiscovery(data);
      setUrl(data.base_url);
    } catch (e) {
      setError(await errorText(e));
    } finally {
      setDiscovering(false);
    }
  };

  const filteredPages = useMemo(() => {
    if (!discovery) return [];
    const q = pageFilter.trim().toLowerCase();
    if (!q) return discovery.pages;
    return discovery.pages.filter((p) => `${p.name} ${p.url} ${p.title}`.toLowerCase().includes(q));
  }, [discovery, pageFilter]);

  const togglePage = (pageUrl) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pageUrl)) next.delete(pageUrl);
      else if (next.size < config.max_pages) next.add(pageUrl);
      return next;
    });
  };

  const selectVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const p of filteredPages) {
        if (next.size >= config.max_pages) break;
        next.add(p.url);
      }
      return next;
    });
  };

  const selectedPages = discovery ? discovery.pages.filter((p) => selected.has(p.url)).map((p) => ({ url: p.url, name: p.name })) : [];
  const estCredits = selectedPages.length * config.est_credits_per_page;
  const estSeconds = Math.ceil(selectedPages.length / Math.max(1, config.concurrency)) * config.est_seconds_per_page;

  const resetNew = () => {
    setUrl('');
    setDiscovery(null);
    setSelected(new Set());
    setSiteName('');
    setSaveSite(true);
    setAgentId('');
    setEditingSite(null);
    setPageFilter('');
  };

  const startOver = () => {
    clearTimeout(pollRef.current);
    storeJob(null);
    setJobId(null);
    setJob(null);
    lastJobRef.current = null;
    setJobVariant('formatted');
    setOpenSite(null);
    setError('');
    setNotice('');
    resetNew();
  };

  const attachJob = (newJobId) => {
    const queued = { status: 'queued', stage: 'Queued', progress: 0, pages: [], ai: { status: 'idle' } };
    lastJobRef.current = queued;
    setJob(queued);
    setJobVariant('formatted');
    storeJob(newJobId);
    setJobId(newJobId);
    setOpenSite(null);
  };

  // ── Start / sync ──────────────────────────────────────────────────────────
  const startScrape = async () => {
    setStarting(true);
    setError('');
    try {
      let response;
      if (editingSite) {
        const name = siteName.trim();
        if (name && name !== editingSite.name) await kbWebScraper.updateSite(editingSite.id, { name });
        response = await kbWebScraper.syncSite(editingSite.id, { pages: selectedPages });
        syncJobsRef.current.add(response.data.job_id);
      } else {
        response = await kbWebScraper.startJob({
          base_url: discovery.base_url,
          pages: selectedPages,
          site_name: siteName.trim() || discovery.site_name,
          save_site: saveSite,
          agent_id: saveSite && agentId ? agentId : null,
        });
      }
      attachJob(response.data.job_id);
      resetNew();
      loadSites();
    } catch (e) {
      setError(await errorText(e));
    } finally {
      setStarting(false);
    }
  };

  const syncSite = async (site) => {
    setBusySite(site.id);
    setError('');
    try {
      const response = await kbWebScraper.syncSite(site.id);
      syncJobsRef.current.add(response.data.job_id);
      attachJob(response.data.job_id);
      loadSites();
    } catch (e) {
      setError(await errorText(e));
    } finally {
      setBusySite('');
    }
  };

  const editSitePages = (site) => {
    setOpenSite(null);
    setEditingSite(site);
    setSiteName(site.name);
    setUrl(site.base_url);
    runDiscovery(site.base_url, site.pages);
  };

  const openSiteFiles = async (site) => {
    if (site.running_job_id) {
      attachJob(site.running_job_id);
      return;
    }
    setBusySite(site.id);
    setError('');
    try {
      const response = await kbWebScraper.getSite(site.id);
      setOpenSite(response.data);
      setSiteVariant(response.data.outputs?.length ? 'formatted' : 'ai');
      resetNew();
    } catch (e) {
      setError(await errorText(e));
    } finally {
      setBusySite('');
    }
  };

  const changeSiteAgent = async (site, value) => {
    try {
      const response = await kbWebScraper.updateSite(site.id, value ? { agent_id: value } : { clear_agent: true });
      setSites((prev) => prev.map((s) => (s.id === site.id ? response.data : s)));
      if (openSite?.id === site.id) setOpenSite((prev) => ({ ...prev, agent_id: response.data.agent_id }));
    } catch (e) {
      setError(await errorText(e));
    }
  };

  const deleteSite = async (site) => {
    setBusySite(site.id);
    try {
      await kbWebScraper.deleteSite(site.id);
      setConfirmDelete(null);
      if (openSite?.id === site.id) setOpenSite(null);
      setNotice(`Removed ${site.name}. Files you already downloaded are not affected.`);
      loadSites();
    } catch (e) {
      setError(await errorText(e));
    } finally {
      setBusySite('');
    }
  };

  const agentKbDone = (result, agentLabel) => {
    setNotice(`Updated ${agentLabel || 'the agent'}'s knowledge base: added ${result.uploaded} file${result.uploaded === 1 ? '' : 's'}, removed ${result.deleted}.`);
    loadSites();
    if (openSite?.id === result.site_id) {
      kbWebScraper.getSite(result.site_id).then((r) => setOpenSite(r.data)).catch(() => {});
    }
  };

  const refreshOpenSite = async (siteId) => {
    if (openSite?.id !== siteId) return;
    try { setOpenSite((await kbWebScraper.getSite(siteId)).data); } catch { /* keep the old view */ }
  };

  const monitorAction = async (site, request, message) => {
    setBusySite(site.id);
    setError('');
    try {
      await request();
      if (message) setNotice(message);
      await refreshOpenSite(site.id);
      loadSites();
    } catch (e) {
      setError(await errorText(e));
    } finally {
      setBusySite('');
    }
  };

  const checkSiteNow = (site) => monitorAction(site, () => kbWebScraper.checkSiteNow(site.id));
  const markSiteSeen = (site) => monitorAction(site, () => kbWebScraper.markSiteSeen(site.id),
    `Marked ${site.name} as seen. Future checks compare against the website as it is now.`);
  const toggleMonitor = (site, enabled) => monitorAction(site, () => kbWebScraper.updateSite(site.id, { monitor_enabled: enabled }));

  const changedSites = sites.filter((s) => s.monitor_enabled && s.monitor_status === 'changed');

  const kbStatusText = (site) => {
    if (!site.kb_pushed_at || !site.kb_file_count) return null;
    const name = agentName(site.kb_agent_id) || 'an agent';
    return `In ${name}'s KB: ${site.kb_file_count} file${site.kb_file_count === 1 ? '' : 's'}, ${formatDate(site.kb_pushed_at)}`;
  };

  // ── Job actions ───────────────────────────────────────────────────────────
  const cancelJob = async () => {
    setCancelling(true);
    try {
      await kbWebScraper.cancelJob(jobId);
    } catch (e) {
      setError(await errorText(e));
    } finally {
      setCancelling(false);
    }
  };

  const download = async (key, request, fallbackName) => {
    setDownloading(key);
    try {
      const response = await request();
      saveBlob(response.data, filenameFrom(response, fallbackName));
    } catch (e) {
      setError(await errorText(e));
    } finally {
      setDownloading('');
    }
  };

  const aiEstimate = job?.estimates?.[model] ?? 0;
  const needsCostConfirm = aiEstimate > (config.confirm_above_usd ?? 5);
  const aiTooLarge = (job?.extracted_tokens || 0) > config.max_ai_tokens;

  const startAi = async () => {
    if (needsCostConfirm && !confirmingCost) {
      setConfirmingCost(true);
      return;
    }
    setConfirmingCost(false);
    setStartingAi(true);
    setError('');
    try {
      await kbWebScraper.optimize(jobId, {
        model,
        generate_faqs: generateFaqs,
        split: 'auto',
        source_name: job.site_name,
        confirm_cost: needsCostConfirm,
      });
      const running = { ...job, ai: { ...(job.ai || {}), status: 'running', stage: 'Starting', progress: 1 } };
      lastJobRef.current = running;
      setJob(running);
      setPollKey((k) => k + 1); // polling stopped when the scrape finished; resume it for the AI step
    } catch (e) {
      setError(await errorText(e));
    } finally {
      setStartingAi(false);
    }
  };

  const isRunning = job && ['queued', 'running'].includes(job.status);
  const pagesDone = job?.pages?.filter((p) => p.status === 'done').length || 0;
  const pagesFailed = job?.pages?.filter((p) => p.status === 'failed' || p.status === 'skipped') || [];
  const aiRunning = job?.ai?.status === 'running';

  const showNew = !job && !openSite;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
            <Globe className="w-5 h-5 text-primary-foreground" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1">KB Web Scraper</h1>
            <p className="text-muted-foreground">Turn a website into structured knowledge base files your agents can retrieve from.</p>
          </div>
        </div>
        {!showNew && (
          <button onClick={startOver} className={secondaryBtn} disabled={isRunning}>
            <Plus className="w-4 h-4" /> New scrape
          </button>
        )}
      </div>

      {!config.configured && (
        <div className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-warning/40 bg-warning/10 text-foreground">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-warning" />
          <div className="text-sm">Website scraping is not set up on this server. Add BROWSERACT_API_KEY, BROWSERACT_WORKFLOW_ID and BROWSERACT_URL_PARAMETER_NAME to the API environment. You can still find pages and open saved websites.</div>
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive">
          <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">{error}</div>
          <button onClick={() => setError('')} aria-label="Dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}
      {notice && (
        <div className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-success/30 bg-success/10 text-foreground">
          <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-success" />
          <div className="flex-1 text-sm">{notice}</div>
          <button onClick={() => setNotice('')} aria-label="Dismiss"><X className="w-4 h-4" /></button>
        </div>
      )}

      {showNew && changedSites.length > 0 && (
        <div className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-warning/40 bg-warning/10 text-foreground">
          <BellRing className="w-5 h-5 flex-shrink-0 mt-0.5 text-warning" />
          <div className="flex-1 text-sm">
            <span className="font-medium">{changedSites.length === 1 ? 'A saved website has' : `${changedSites.length} saved websites have`} changed since the last sync: </span>
            {changedSites.map((s, i) => (
              <span key={s.id}>
                {i > 0 && ', '}
                <button onClick={() => openSiteFiles(s)} className="text-primary underline-offset-2 hover:underline">{s.name}</button>
              </span>
            ))}
            . Sync {changedSites.length === 1 ? 'it' : 'them'} to keep the agent's knowledge base current.
          </div>
        </div>
      )}

      {/* Step 1 + 2: find pages, pick, options */}
      {showNew && (
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6">
          <div className={`${cardClass} flex flex-col`}>
            <SectionTitle
              icon={Globe}
              right={editingSite && (
                <button onClick={resetNew} className={secondaryBtn}><X className="w-4 h-4" /> Cancel editing</button>
              )}
            >
              {editingSite ? `Pages for ${editingSite.name}` : 'Website'}
            </SectionTitle>

            {!editingSite && (
              <form
                className="flex gap-2"
                onSubmit={(e) => { e.preventDefault(); if (url.trim()) runDiscovery(url.trim()); }}
              >
                <input
                  className={inputClass}
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="example.com"
                  disabled={discovering}
                />
                <button type="submit" disabled={!url.trim() || discovering} className={`${primaryBtn} whitespace-nowrap`}>
                  {discovering ? <LoadingSpinner size="sm" /> : <Search className="w-4 h-4" />} Find pages
                </button>
              </form>
            )}
            {!discovery && !discovering && !editingSite && (
              <p className="text-xs text-muted-foreground mt-3">
                We read the site's sitemap and links to list its pages. Finding pages is free; scraping uses BrowserAct credits (about {config.est_credits_per_page} per page).
              </p>
            )}
            {discovering && <div className="mt-6"><LoadingSpinner size="sm" text="Reading the sitemap and following links. This takes up to 40 seconds..." /></div>}

            {discovery && (
              <div className="mt-5 flex flex-col min-h-0">
                <div className="text-sm text-muted-foreground mb-3">
                  Found <span className="font-semibold text-foreground">{discovery.count}</span> pages on {discovery.base_url}
                  {discovery.from_sitemap > 0 && ` (${discovery.from_sitemap} in the sitemap)`}
                  {discovery.truncated && '. The list is capped, so filter to find a page that is missing.'}
                </div>
                <div className="flex gap-2 mb-3 flex-wrap">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      value={pageFilter}
                      onChange={(e) => setPageFilter(e.target.value)}
                      placeholder="Filter pages, e.g. pricing"
                      className="w-full pl-9 pr-3 py-2 text-sm bg-muted/30 border border-border/60 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                  <button onClick={selectVisible} className={secondaryBtn} disabled={selected.size >= config.max_pages}>
                    Select {pageFilter ? 'matching' : 'all'}
                  </button>
                  <button onClick={() => setSelected(new Set())} className={secondaryBtn} disabled={!selected.size}>Clear</button>
                </div>
                <div className="border border-border/60 rounded-lg divide-y divide-border/40 overflow-y-auto" style={{ maxHeight: '420px' }}>
                  {filteredPages.map((p) => {
                    const checked = selected.has(p.url);
                    const atLimit = !checked && selected.size >= config.max_pages;
                    return (
                      <label key={p.url} className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-muted/30 ${atLimit ? 'opacity-50' : ''}`}>
                        <input type="checkbox" className="mt-1 w-4 h-4 accent-[hsl(var(--primary))]" checked={checked} disabled={atLimit} onChange={() => togglePage(p.url)} />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-foreground truncate" title={p.title || p.name}>
                            {p.name}
                            {p.saved_only && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-warning/20 text-foreground">not found on the site now</span>}
                          </span>
                          <span className="block text-xs text-muted-foreground truncate">{p.url.replace(/^https?:\/\//, '')}</span>
                        </span>
                      </label>
                    );
                  })}
                  {!filteredPages.length && <div className="p-4 text-sm text-muted-foreground">No pages match this filter.</div>}
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  {selected.size} of {config.max_pages} pages selected per run.
                </div>
              </div>
            )}
          </div>

          <div className={`${cardClass} flex flex-col`}>
            <SectionTitle icon={Sparkles}>Options</SectionTitle>
            <div className="space-y-4 flex-1">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Brand or website name</label>
                <input className={inputClass} value={siteName} onChange={(e) => setSiteName(e.target.value)} placeholder="Detected from the website" />
                <p className="text-xs text-muted-foreground mt-1">Prefixed to every topic so each chunk names who it is about.</p>
              </div>
              {!editingSite && (
                <>
                  <Toggle checked={saveSite} onChange={setSaveSite} label="Save this website"
                    hint="Keeps the page list and newest files so you can sync later without picking pages again." />
                  {saveSite && agentList.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2"><Bot className="w-4 h-4" /> Agent (optional)</label>
                      <AnimatedDropdown
                        value={agentId}
                        onChange={(value) => setAgentId(value)}
                        options={[{ value: '', label: 'No agent' }, ...agentList.map((a) => ({ value: a.id, label: a.name }))]}
                        placeholder="No agent"
                        icon={Bot}
                      />
                      <p className="text-xs text-muted-foreground mt-1">The agent whose knowledge base these files can update. Nothing goes into it until you review the changes and confirm.</p>
                    </div>
                  )}
                </>
              )}

              {selectedPages.length > 0 && (
                <div className="rounded-lg border border-border/60 bg-muted/20 p-4 text-sm space-y-1">
                  <div className="flex items-center gap-2 text-foreground"><Coins className="w-4 h-4 text-primary" /> About {estCredits.toLocaleString()} BrowserAct credits ({selectedPages.length} page{selectedPages.length === 1 ? '' : 's'} × ~{config.est_credits_per_page})</div>
                  <div className="flex items-center gap-2 text-foreground"><Clock className="w-4 h-4 text-primary" /> Takes {minutesText(estSeconds)}, {config.concurrency} pages at a time</div>
                  <p className="text-xs text-muted-foreground pt-1">Formatting is free. You can run the AI optimizer on the result afterwards.</p>
                </div>
              )}
            </div>
            <button
              onClick={startScrape}
              disabled={!discovery || !selectedPages.length || starting || !config.configured}
              className={`${primaryBtn} w-full mt-6`}
            >
              {starting ? <><LoadingSpinner size="sm" /> Starting...</> : (
                <><Globe className="w-4 h-4" /> {editingSite ? 'Save pages and sync' : 'Scrape'} {selectedPages.length || ''} page{selectedPages.length === 1 ? '' : 's'}</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: progress */}
      {job && isRunning && (
        <div className={`${cardClass} max-w-3xl`}>
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <LoadingSpinner size="sm" />
              <h3 className="text-lg font-semibold text-foreground">Scraping {job.site_name || 'the website'}</h3>
            </div>
            <button onClick={cancelJob} className={secondaryBtn} disabled={cancelling || job.cancel_requested}>
              <Ban className="w-4 h-4" /> {job.cancel_requested ? 'Stopping...' : 'Cancel'}
            </button>
          </div>
          <div className="w-full h-2 rounded-full bg-muted/50 overflow-hidden">
            <div className="h-full bg-gradient-button transition-all duration-500" style={{ width: `${Math.max(3, job.progress || 0)}%` }} />
          </div>
          <div className="flex justify-between text-sm text-muted-foreground mt-2">
            <span>{job.stage}</span>
            <span>{job.progress || 0}%</span>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Each page opens in a real browser through BrowserAct, which takes a minute or two per page. You can leave this page and come back.
            {job.cancel_requested && ' Pages already in progress will finish and be kept.'}
          </p>
          {job.pages?.length > 0 && (
            <div className="mt-4 border border-border/60 rounded-lg divide-y divide-border/40 overflow-y-auto" style={{ maxHeight: '360px' }}>
              {job.pages.map((p) => (
                <div key={p.url} className="flex items-center gap-3 px-3 py-2 text-sm">
                  {p.status === 'running' ? <LoadingSpinner size="sm" /> : p.status === 'done' ? <CheckCircle className="w-4 h-4 text-success" /> : p.status === 'failed' ? <XCircle className="w-4 h-4 text-destructive" /> : <Clock className="w-4 h-4 text-muted-foreground" />}
                  <span className="flex-1 min-w-0 truncate text-foreground" title={p.url}>{p.name}</span>
                  <span className={`text-xs ${PAGE_STATUS[p.status]?.className}`}>{PAGE_STATUS[p.status]?.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {job?.status === 'failed' && (
        <div className={`${cardClass} max-w-3xl`}>
          <div className="flex items-start gap-3">
            <XCircle className="w-6 h-6 text-destructive flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-foreground">Scrape failed</h3>
              <p className="text-sm text-muted-foreground mt-1">{job.error}</p>
              {job.site_id && <p className="text-xs text-muted-foreground mt-1">The saved website keeps its previous files.</p>}
              {pagesFailed.length > 0 && (
                <ul className="mt-3 space-y-1 text-xs">
                  {pagesFailed.map((p) => <li key={p.url} className="break-all"><span className="text-foreground">{p.url}</span>: <span className="text-muted-foreground">{p.error}</span></li>)}
                </ul>
              )}
              <button onClick={startOver} className={`${secondaryBtn} mt-4`}><RotateCcw className="w-4 h-4" /> Start over</button>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: results */}
      {job?.status === 'completed' && (
        <div className="grid grid-cols-1 2xl:grid-cols-[340px_1fr] gap-6 min-h-0">
          <div className="space-y-4">
            <div className={cardClass}>
              <div className="flex items-center gap-2 mb-1">
                {pagesFailed.length ? <AlertTriangle className="w-5 h-5 text-warning" /> : <CheckCircle className="w-5 h-5 text-success" />}
                <h3 className="text-lg font-semibold text-foreground">{pagesDone} of {job.pages.length} pages scraped</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                {job.site_name} · {job.seconds}s · {job.credits_used?.toLocaleString?.() ?? job.credits_used} BrowserAct credits
                {job.site_id && ' · saved'}
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => download('job-formatted', () => kbWebScraper.downloadJob(jobId, 'formatted'), 'website_kb.zip')}
                  disabled={downloading === 'job-formatted'}
                  className={`${primaryBtn} w-full`}
                >
                  {downloading === 'job-formatted' ? <LoadingSpinner size="sm" /> : <Download className="w-4 h-4" />} Download formatted files (.zip)
                </button>
                <button
                  onClick={() => download('job-extracted', () => kbWebScraper.downloadExtracted(jobId), 'website_extracted.txt')}
                  disabled={downloading === 'job-extracted'}
                  className={`${secondaryBtn} w-full justify-center`}
                >
                  <FileText className="w-4 h-4" /> Download plain extracted text
                </button>
              </div>
              {job.site_id ? (
                <button
                  onClick={() => setReviewSite({ site: { id: job.site_id, name: job.site_name, agent_id: sites.find((s) => s.id === job.site_id)?.agent_id || null }, variant: jobVariant })}
                  disabled={aiRunning}
                  className={`${secondaryBtn} w-full justify-center mt-2`}
                >
                  <Bot className="w-4 h-4" /> Review and update agent KB
                </button>
              ) : (
                <p className="text-xs text-muted-foreground mt-3">Upload the .txt files on the Knowledge Base page and assign them to your agent.</p>
              )}
              {pagesFailed.length > 0 && (
                <div className="mt-4 rounded-lg border border-warning/40 bg-warning/10 p-3">
                  <p className="text-xs font-semibold text-foreground mb-1">Left out of the files:</p>
                  <ul className="space-y-1 text-xs">
                    {pagesFailed.map((p) => <li key={p.url} className="break-all"><span className="text-foreground">{p.name}</span>: <span className="text-muted-foreground">{p.error}</span></li>)}
                  </ul>
                </div>
              )}
            </div>

            <div className={cardClass}>
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">Optimize with AI</h3>
              </div>
              {aiRunning ? (
                <>
                  <div className="w-full h-2 rounded-full bg-muted/50 overflow-hidden">
                    <div className="h-full bg-gradient-button transition-all duration-500" style={{ width: `${Math.max(3, job.ai.progress || 0)}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-2">
                    <span>{job.ai.stage}</span><span>{job.ai.progress || 0}%</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">Rewriting the pages into self-contained topics and checking that prices, links and contact details survived. This takes a few minutes.</p>
                </>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    Optional. Sends the scraped text through the KB Optimizer for cleaner topics and likely customer questions. The formatted files above are ready to use without it.
                  </p>
                  {job.ai?.status === 'failed' && <p className="text-xs text-destructive mb-3">Last attempt failed: {job.ai.error}</p>}
                  {job.ai?.status === 'completed' && job.ai.usage && (
                    <p className="text-xs text-success mb-3">Done in {job.ai.usage.seconds}s using {job.ai.usage.model}, AI cost ~${job.ai.usage.cost_usd?.toFixed(2)}.</p>
                  )}
                  <div className="space-y-3">
                    <AnimatedDropdown
                      value={model}
                      onChange={(value) => setModel(value)}
                      options={config.models.map((m) => ({ value: m.id, label: m.label }))}
                      placeholder="Select a model"
                      icon={Cpu}
                    />
                    <Toggle checked={generateFaqs} onChange={setGenerateFaqs} label="Add likely customer questions"
                      hint="A few Q and A pairs per topic, answered only from the website's text." />
                    <p className="text-xs text-muted-foreground">
                      About {(job.extracted_tokens || 0).toLocaleString()} tokens of text. Estimated cost ~${aiEstimate < 0.01 ? '0.01' : aiEstimate.toFixed(2)}.
                    </p>
                    {aiTooLarge && (
                      <p className="text-xs text-destructive">That is above the {config.max_ai_tokens.toLocaleString()} token AI limit per run. Scrape fewer pages to use the AI step.</p>
                    )}
                  </div>
                  {confirmingCost && (
                    <div className="mt-3 rounded-lg border border-warning/40 bg-warning/10 p-3 text-xs text-foreground">
                      <p className="font-semibold">This run will cost about ${aiEstimate.toFixed(2)}</p>
                      <div className="flex gap-2 mt-2">
                        <button onClick={startAi} className={secondaryBtn}>Continue</button>
                        <button onClick={() => setConfirmingCost(false)} className={secondaryBtn}>Cancel</button>
                      </div>
                    </div>
                  )}
                  <button onClick={startAi} disabled={startingAi || aiTooLarge || confirmingCost} className={`${secondaryBtn} w-full justify-center mt-4`}>
                    {startingAi ? <LoadingSpinner size="sm" /> : <Sparkles className="w-4 h-4" />}
                    {job.ai?.status === 'completed' ? 'Run AI again' : 'Optimize with AI'}
                  </button>
                </>
              )}
              {job.ai?.status === 'completed' && job.ai.outputs?.length > 0 && (
                <button
                  onClick={() => download('job-ai', () => kbWebScraper.downloadJob(jobId, 'ai'), 'website_kb_ai.zip')}
                  disabled={downloading === 'job-ai'}
                  className={`${primaryBtn} w-full mt-3`}
                >
                  {downloading === 'job-ai' ? <LoadingSpinner size="sm" /> : <Download className="w-4 h-4" />} Download AI files (.zip)
                </button>
              )}
            </div>
          </div>

          <div className="min-h-0">
            <VariantTabs variant={jobVariant} onChange={setJobVariant} hasAi={job.ai?.outputs?.length > 0} />
            <FilesViewer
              files={jobVariant === 'ai' ? job.ai?.outputs : job.outputs}
              emptyText="No files yet."
            />
          </div>
        </div>
      )}

      {/* Saved website files */}
      {openSite && !job && (
        <div className="space-y-4">
          <div className={cardClass}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <h3 className="text-xl font-semibold text-foreground">{openSite.name}</h3>
                <a href={openSite.base_url} target="_blank" rel="noreferrer" className="text-sm text-primary break-all">{openSite.base_url}</a>
                <p className="text-xs text-muted-foreground mt-1">
                  {openSite.pages.length} saved page{openSite.pages.length === 1 ? '' : 's'} · last scraped {formatDate(openSite.last_scraped_at)}
                  {openSite.agent_id && agentName(openSite.agent_id) && ` · for ${agentName(openSite.agent_id)}`}
                </p>
                {kbStatusText(openSite) && <p className="text-xs text-success mt-1">{kbStatusText(openSite)}{openSite.kb_variant === 'ai' ? ' (AI-optimized)' : ''}</p>}
                {openSite.last_status === 'failed' && <p className="text-xs text-destructive mt-1">Last sync failed: {openSite.last_error}. These are the files from the sync before it.</p>}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => syncSite(openSite)} disabled={busySite === openSite.id || !config.configured} className={secondaryBtn}>
                  <RefreshCw className="w-4 h-4" /> Sync now
                </button>
                <button onClick={() => editSitePages(openSite)} className={secondaryBtn}><Pencil className="w-4 h-4" /> Edit pages</button>
                <button
                  onClick={() => setReviewSite({ site: openSite, variant: siteVariant })}
                  disabled={!(siteVariant === 'ai' ? openSite.ai_outputs : openSite.outputs)?.length}
                  className={secondaryBtn}
                >
                  <Bot className="w-4 h-4" /> Update agent KB
                </button>
                <button
                  onClick={() => download('site', () => kbWebScraper.downloadSite(openSite.id, siteVariant), 'website_kb.zip')}
                  disabled={downloading === 'site' || !(siteVariant === 'ai' ? openSite.ai_outputs : openSite.outputs)?.length}
                  className={secondaryBtn}
                >
                  <Download className="w-4 h-4" /> Download {siteVariant === 'ai' ? 'AI files' : 'files'}
                </button>
              </div>
            </div>
            {openSite.failed_pages?.length > 0 && (
              <div className="mt-4 rounded-lg border border-warning/40 bg-warning/10 p-3">
                <p className="text-xs font-semibold text-foreground mb-1">Pages that failed in the last sync:</p>
                <ul className="space-y-1 text-xs">
                  {openSite.failed_pages.map((p) => <li key={p.url} className="break-all"><span className="text-foreground">{p.url}</span>: <span className="text-muted-foreground">{p.error}</span></li>)}
                </ul>
              </div>
            )}
          </div>
          <MonitorCard
            site={openSite}
            busy={busySite === openSite.id}
            canSync={busySite !== openSite.id && config.configured}
            onCheck={() => checkSiteNow(openSite)}
            onSeen={() => markSiteSeen(openSite)}
            onToggle={(enabled) => toggleMonitor(openSite, enabled)}
            onSync={() => syncSite(openSite)}
          />
          <div>
            <VariantTabs variant={siteVariant} onChange={setSiteVariant} hasAi={openSite.ai_outputs?.length > 0} aiStale={openSite.ai_stale} />
            {siteVariant === 'ai' && openSite.ai_stale && (
              <p className="text-xs text-muted-foreground mb-3">These AI files were made on {formatDate(openSite.ai_generated_at)}, before the newest sync. Run a sync and then Optimize with AI to refresh them.</p>
            )}
            <FilesViewer
              files={siteVariant === 'ai' ? openSite.ai_outputs : openSite.outputs}
              emptyText="This website has no files yet. Sync it to scrape the saved pages."
            />
          </div>
        </div>
      )}

      {/* Saved websites */}
      {showNew && (
        <div className={`${cardClass} mt-6`}>
          <SectionTitle icon={FolderOpen}>Saved websites</SectionTitle>
          {sitesLoading ? (
            <LoadingSpinner size="sm" text="Loading..." />
          ) : !sites.length ? (
            <p className="text-sm text-muted-foreground">No saved websites yet. Websites you scrape with "Save this website" on appear here so you can sync them later.</p>
          ) : (
            <div className="divide-y divide-border/40">
              {sites.map((site) => (
                <div key={site.id} className="py-4 flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-foreground">{site.name}</span>
                      {site.running_job_id ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/15 text-primary">Syncing</span>
                      ) : site.monitor_enabled && site.monitor_status === 'changed' ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-warning/20 text-warning flex items-center gap-1"><BellRing className="w-3 h-3" /> Website changed</span>
                      ) : site.last_status && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[site.last_status] || ''}`}>{STATUS_LABELS[site.last_status] || site.last_status}</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground break-all">{site.base_url}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {site.pages.length} page{site.pages.length === 1 ? '' : 's'} · {site.files.length} file{site.files.length === 1 ? '' : 's'}
                      {site.ai_files.length > 0 && ` + ${site.ai_files.length} AI`} · last scraped {formatDate(site.last_scraped_at)}
                    </div>
                    <div className={`text-xs mt-1 ${monitorInfo(site).className}`}>{monitorInfo(site).text}</div>
                    {site.kb_replace_running ? (
                      <div className="text-xs text-primary mt-1">Updating the agent's knowledge base...</div>
                    ) : kbStatusText(site) && (
                      <div className="text-xs text-success mt-1">{kbStatusText(site)}</div>
                    )}
                  </div>
                  <div className="w-56">
                    <AnimatedDropdown
                      value={site.agent_id || ''}
                      onChange={(value) => changeSiteAgent(site, value)}
                      options={[{ value: '', label: 'No agent' }, ...agentList.map((a) => ({ value: a.id, label: a.name }))]}
                      placeholder="No agent"
                      icon={Bot}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openSiteFiles(site)} disabled={busySite === site.id} className={iconBtn} title={site.running_job_id ? 'Show progress' : 'View files'}>
                      {busySite === site.id ? <LoadingSpinner size="sm" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button onClick={() => syncSite(site)} disabled={busySite === site.id || !!site.running_job_id || !config.configured} className={iconBtn} title="Sync now: re-scrape the saved pages, then review the changes before they go into the agent">
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setReviewSite({ site, variant: 'formatted' })}
                      disabled={!site.files.length || !!site.running_job_id}
                      className={iconBtn}
                      title="Review the changes and update the agent's knowledge base"
                    >
                      <Bot className="w-4 h-4" />
                    </button>
                    <button onClick={() => editSitePages(site)} disabled={!!site.running_job_id} className={iconBtn} title="Change the pages">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => download(`site-${site.id}`, () => kbWebScraper.downloadSite(site.id, 'formatted'), 'website_kb.zip')}
                      disabled={!site.files.length || downloading === `site-${site.id}`}
                      className={iconBtn}
                      title="Download files (.zip)"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    {confirmDelete === site.id ? (
                      <span className="flex items-center gap-1 text-xs">
                        <button onClick={() => deleteSite(site)} className="px-2 py-1 rounded bg-destructive text-destructive-foreground">Remove</button>
                        <button onClick={() => setConfirmDelete(null)} className="px-2 py-1 rounded border border-border/60">Keep</button>
                      </span>
                    ) : (
                      <button onClick={() => setConfirmDelete(site.id)} className={`${iconBtn} hover:text-destructive`} title="Remove saved website">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {reviewSite && (
        <KbAgentReviewModal
          key={`${reviewSite.site.id}-${reviewSite.variant}`}
          site={reviewSite.site}
          initialVariant={reviewSite.variant}
          agentList={agentList}
          onClose={() => { setReviewSite(null); loadSites(); }}
          onDone={agentKbDone}
        />
      )}
    </div>
  );
};

export default KbWebScraper;
